// sessionLive.ts — the sessions page's sockets, held OUTSIDE React.
//
// Two kinds of connection, because two different servers know two
// different halves of the answer:
//
//   the manager socket   /api/ws — the caller's session list and every
//                        change to it, plus each row's live pod phase.
//                        The manager owns the rows and the Pods, so it
//                        is the only thing that can say a session is
//                        still provisioning: there is nothing running
//                        yet to ask.
//
//   a harness socket     one per session, to that session's Pod. The
//   per session          harness owns what is happening inside the
//                        conversation — whether a turn is running, what
//                        tool it is in, the tokens as they arrive.
//
// Held outside React for the same reason channelStore is: a socket owned
// by component state dies on every remount, and this page remounts its
// list rows constantly as titles regenerate. Here a re-render is free and
// the connections survive it.
//
// The sockets to sessions the user is not looking at connect with
// `?filter=status`, so the browser carries turn brackets and tool names
// for a dozen conversations rather than every token of all of them.

export type SocketStatus = 'connecting' | 'open' | 'closed' | 'error';

export type SessionPhase = 'provisioning' | 'ready' | 'suspended';

/** Reconnect backoff, in ms. Capped so a dead Pod isn't hammered. */
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15000;

/** One session's live state, as the sidebar renders it. */
export interface SessionActivity {
    /** What the harness says it is doing. 'unknown' until it says. */
    status: 'idle' | 'running' | 'cancelling' | 'unknown';
    /** The running turn, when there is one. */
    activeTurnId: string;
    /** Health of this session's harness socket. */
    socket: SocketStatus;
    /** Highest bus sequence seen, so a reconnect resumes rather than replays. */
    lastSeq: number;
    /**
     * A short live label — the tool the turn is in, or the harness's own
     * status line. Cleared when the turn ends. This is what makes a
     * session in the sidebar legible while the user is reading another
     * one.
     */
    note: string;
    /**
     * The harness's own status line for the running turn, and nothing
     * else: "Model unavailable … retrying in 40s", "Resuming after an
     * interruption". Unlike `note` it is not overwritten by tool names,
     * so the open conversation can show it under the transcript.
     */
    statusLine: string;
}

export interface LiveSnapshot {
    /** Health of the socket to the manager. */
    manager: SocketStatus;
    phases: Record<string, SessionPhase>;
    activity: Record<string, SessionActivity>;
}

const IDLE: SessionActivity = {
    status: 'unknown',
    activeTurnId: '',
    socket: 'closed',
    lastSeq: 0,
    note: '',
    statusLine: '',
};

const EMPTY: LiveSnapshot = { manager: 'closed', phases: {}, activity: {} };

// ── frames ──────────────────────────────────────────────────────────

/** One event from a session's harness. Mirrors sessionenv's /api/ws. */
export interface HarnessFrame {
    seq?: number;
    event: string;
    data?: unknown;
    turn_id?: string;
}

/** What the page does with a focused session's frames. */
export interface HarnessHandlers {
    onState?: (state: Record<string, unknown>) => void;
    onTurnStart?: (turnId: string, prompt: string) => void;
    /** A message added to the turn already running, from any client. */
    onSteer?: (message: string) => void;
    onDelta?: (text: string) => void;
    onThinking?: (text: string) => void;
    onToolUi?: (toolCallId: string, markdown: string) => void;
    onToolStarted?: (toolCallId: string, name: string, input: unknown) => void;
    onToolCompleted?: (toolCallId: string, output: string, isError: boolean) => void;
    onError?: (message: string) => void;
    onTurnEnd?: (turnId: string) => void;
}

/** What the page does with the manager's list frames. */
export interface ManagerHandlers<S, W = unknown> {
    onSessions?: (sessions: S[]) => void;
    onCreated?: (session: S) => void;
    onUpdated?: (session: S) => void;
    onDeleted?: (id: string) => void;
    /** The workspace list, from the same snapshot frame as the sessions. */
    onWorkspaces?: (workspaces: W[]) => void;
    /** A workspace created or changed; rows arrive whole, applied by id. */
    onWorkspaceUpserted?: (workspace: W) => void;
    onWorkspaceDeleted?: (id: string) => void;
}

// ── store ───────────────────────────────────────────────────────────

interface Socket {
    ws: WebSocket | null;
    url: string;
    attempt: number;
    timer: number | null;
    closedByUs: boolean;
}

interface HarnessEntry extends Socket {
    /** 'full' carries the per-token events; 'status' does not. */
    mode: 'full' | 'status';
}

let snapshot: LiveSnapshot = EMPTY;
const listeners = new Set<() => void>();

const harnesses = new Map<string, HarnessEntry>();
let manager: Socket | null = null;
let managerHandlers: ManagerHandlers<any, any> = {};
let token = '';

/** Handlers for the one session whose transcript is on screen. */
let focusedId = '';
let focusedHandlers: HarnessHandlers = {};

/** Sessions whose transcript the page is rendering from somewhere else. */
const suppressed = new Set<string>();

/**
 * Stop delivering transcript frames for one session, without closing its
 * socket or losing its sidebar state.
 *
 * For the fallback send path. When the socket is down the page streams a
 * turn from its own POST response instead — and the harness publishes
 * that turn to its bus like any other, so a socket that reconnects
 * part-way through would replay into a bubble the response is already
 * filling. Status still flows: which session is busy is not something
 * the page should lose just because it is rendering the text itself.
 */
export function suppressTranscript(sessionId: string, on: boolean): void {
    if (on) suppressed.add(sessionId);
    else suppressed.delete(sessionId);
}

function emit(): void {
    for (const listener of listeners) listener();
}

/** Replace the snapshot by identity — useSyncExternalStore compares by reference. */
function patch(next: Partial<LiveSnapshot>): void {
    snapshot = { ...snapshot, ...next };
    emit();
}

function patchActivity(id: string, next: Partial<SessionActivity>): void {
    const current = snapshot.activity[id] ?? IDLE;
    patch({ activity: { ...snapshot.activity, [id]: { ...current, ...next } } });
}

function backoff(entry: Socket): number {
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** entry.attempt, RECONNECT_MAX_MS);
    entry.attempt += 1;
    return delay;
}

/**
 * Open a socket, handing the token over as a subprotocol.
 *
 * A browser cannot set an Authorization header on a WebSocket and this
 * SPA authenticates primarily by Bearer — the cookie is the fallback. So
 * the token rides `Sec-WebSocket-Protocol`, which both the manager
 * (wsBearer) and the Pod (_bearer_subprotocol) unpack. Deliberately not
 * a query parameter: those end up in proxy logs.
 */
function dial(url: string): WebSocket | null {
    const absolute = new URL(url, window.location.origin);
    absolute.protocol = absolute.protocol === 'https:' ? 'wss:' : 'ws:';
    try {
        return token
            ? new WebSocket(absolute.toString(), ['bearer', token])
            : new WebSocket(absolute.toString());
    } catch {
        return null;
    }
}

// ── the manager socket ──────────────────────────────────────────────

/**
 * Connect to the manager, or adopt the existing connection.
 *
 * Idempotent: the sessions page mounts, unmounts and remounts as the
 * user navigates, and each visit should find the socket already up.
 */
export function connectManager<S, W = unknown>(
    url: string,
    authToken: string,
    handlers: ManagerHandlers<S, W>,
): void {
    token = authToken || token;
    managerHandlers = handlers;
    if (manager?.ws) return;
    manager = manager ?? {
        ws: null, url, attempt: 0, timer: null, closedByUs: false,
    };
    manager.url = url;
    openManager();
}

function openManager(): void {
    if (!manager) return;
    const ws = dial(manager.url);
    if (!ws) {
        patch({ manager: 'error' });
        return;
    }
    manager.ws = ws;
    manager.closedByUs = false;
    patch({ manager: 'connecting' });

    ws.onopen = () => {
        if (manager) manager.attempt = 0;
        patch({ manager: 'open' });
    };
    ws.onmessage = (event) => {
        try {
            applyManagerFrame(JSON.parse(String(event.data)));
        } catch {
            // One frame lost, not a dead socket.
        }
    };
    ws.onerror = () => patch({ manager: 'error' });
    ws.onclose = () => {
        if (!manager) return;
        manager.ws = null;
        if (manager.closedByUs) return;
        patch({ manager: 'closed' });
        const delay = backoff(manager);
        manager.timer = window.setTimeout(() => {
            if (manager) manager.timer = null;
            openManager();
        }, delay);
    };
}

function applyManagerFrame(frame: any): void {
    switch (frame?.event) {
        case 'sessions':
            // The snapshot is authoritative: a reconnect after a drop
            // replaces the list rather than patching a base that may
            // have missed frames while the socket was down.
            managerHandlers.onSessions?.(frame.sessions ?? []);
            // A manager from before workspaces sends none; leaving the
            // list alone then is better than wiping it.
            if (Array.isArray(frame.workspaces)) {
                managerHandlers.onWorkspaces?.(frame.workspaces);
            }
            break;
        case 'workspace_created':
        case 'workspace_updated':
            if (frame.workspace) managerHandlers.onWorkspaceUpserted?.(frame.workspace);
            break;
        case 'workspace_deleted':
            if (frame.id) {
                managerHandlers.onWorkspaceDeleted?.(frame.id);
                dropPhase(frame.id);
            }
            break;
        case 'session_created':
            if (frame.session) managerHandlers.onCreated?.(frame.session);
            break;
        case 'session_updated':
            if (frame.session) managerHandlers.onUpdated?.(frame.session);
            break;
        case 'session_deleted':
            if (frame.id) {
                managerHandlers.onDeleted?.(frame.id);
                dropPhase(frame.id);
            }
            break;
        // A workspace's phase and its sessions' arrive separately but in
        // one map: ids do not collide, and a session's is its workspace's.
        case 'session_phase':
        case 'workspace_phase':
            if (frame.id && frame.phase) {
                patch({ phases: { ...snapshot.phases, [frame.id]: frame.phase } });
            }
            break;
        default:
            // An unknown event is a manager newer than this SPA. The
            // forward-compat contract is that we ignore it.
            break;
    }
}

function dropPhase(id: string): void {
    if (!(id in snapshot.phases)) return;
    const phases = { ...snapshot.phases };
    delete phases[id];
    patch({ phases });
}

export function disconnectManager(): void {
    if (!manager) return;
    manager.closedByUs = true;
    if (manager.timer !== null) window.clearTimeout(manager.timer);
    manager.ws?.close();
    manager = null;
    patch({ manager: 'closed' });
}

// ── the per-session harness sockets ─────────────────────────────────

/** Where a session's harness socket lives, relative to the SPA. */
export function harnessUrl(sessionId: string): string {
    return `/api/sessions/${sessionId}/chat/api/ws`;
}

/**
 * Reconcile the open harness sockets against what the page is showing.
 *
 * Called on every list or selection change, and cheap when nothing moved
 * — an already-open socket in the right mode is left strictly alone,
 * which is what stops a re-render from cycling a dozen connections.
 *
 * `ready` is the subset whose Pod is actually up. Dialing the rest would
 * just queue failed handshakes against a Service with no endpoints; they
 * connect when the manager reports them ready, which is exactly the
 * event the manager socket exists to deliver.
 */
export function syncHarnesses(
    ready: readonly string[],
    focused: string,
    authToken = '',
): void {
    token = authToken || token;
    const wanted = new Set(ready);

    for (const [id, entry] of harnesses) {
        if (!wanted.has(id)) {
            closeHarness(id, entry);
            continue;
        }
        const mode = id === focused ? 'full' : 'status';
        if (entry.mode !== mode) {
            // The filter is fixed at handshake, so a change of mode is a
            // new socket. Resuming from lastSeq is what keeps the switch
            // lossless: the frames the status socket filtered out are
            // still in the harness's replay buffer, so opening the
            // conversation replays the turn in flight rather than
            // starting from whatever arrives next.
            closeHarness(id, entry);
            openHarness(id, mode);
        }
    }
    for (const id of wanted) {
        if (!harnesses.has(id)) openHarness(id, id === focused ? 'full' : 'status');
    }
}

/**
 * Point the transcript handlers at one session.
 *
 * Separate from `syncHarnesses` because the two change at different
 * times: the handlers are rebuilt on every render (they close over
 * component state), while the sockets must not be.
 */
export function setFocus(sessionId: string, handlers: HarnessHandlers): void {
    focusedId = sessionId;
    focusedHandlers = handlers;
}

function openHarness(id: string, mode: 'full' | 'status'): void {
    const previous = snapshot.activity[id];
    const entry: HarnessEntry = harnesses.get(id) ?? {
        ws: null, url: harnessUrl(id), attempt: 0, timer: null,
        closedByUs: false, mode,
    };
    entry.mode = mode;
    harnesses.set(id, entry);

    const url = new URL(harnessUrl(id), window.location.origin);
    if (mode === 'status') url.searchParams.set('filter', 'status');
    // Resume where this session left off. Without a cursor the harness
    // picks the start of the running turn, which is the right answer for
    // a first connection and the wrong one for a reconnect that already
    // rendered half of it.
    if (previous?.lastSeq) url.searchParams.set('last_seq', String(previous.lastSeq));

    const ws = dial(url.pathname + url.search);
    if (!ws) {
        patchActivity(id, { socket: 'error' });
        return;
    }
    entry.ws = ws;
    entry.closedByUs = false;
    patchActivity(id, { socket: 'connecting' });

    ws.onopen = () => {
        entry.attempt = 0;
        patchActivity(id, { socket: 'open' });
    };
    ws.onmessage = (event) => {
        try {
            const frame = JSON.parse(String(event.data));
            if (frame?.keepalive) return;
            applyHarnessFrame(id, frame as HarnessFrame);
        } catch {
            // One frame lost, not a dead socket.
        }
    };
    ws.onerror = () => patchActivity(id, { socket: 'error' });
    ws.onclose = () => {
        entry.ws = null;
        if (entry.closedByUs) return;
        patchActivity(id, { socket: 'closed' });
        entry.timer = window.setTimeout(() => {
            entry.timer = null;
            if (harnesses.has(id)) openHarness(id, entry.mode);
        }, backoff(entry));
    };
}

function closeHarness(id: string, entry: HarnessEntry): void {
    entry.closedByUs = true;
    if (entry.timer !== null) window.clearTimeout(entry.timer);
    entry.ws?.close();
    harnesses.delete(id);
    patchActivity(id, { socket: 'closed' });
}

/**
 * Forget where a session's stream had got to, so its next connection
 * replays the running turn from the beginning.
 *
 * Called when the page reloads a session's transcript from
 * `/api/history`. That transcript does not contain the partial reply of
 * a turn still in flight — the harness commits an assistant message only
 * once the turn ends — so resuming from a cursor would leave the text
 * produced before the reload missing from the page for good. Replaying
 * the whole turn costs a few kilobytes and is always right.
 */
export function resetCursor(sessionId: string): void {
    if (!snapshot.activity[sessionId]) return;
    patchActivity(sessionId, { lastSeq: 0 });
}

/** Close every harness socket and the manager one — the page is leaving. */
export function disconnectAll(): void {
    for (const [id, entry] of [...harnesses]) closeHarness(id, entry);
    disconnectManager();
    suppressed.clear();
    snapshot = EMPTY;
    emit();
}

function applyHarnessFrame(id: string, frame: HarnessFrame): void {
    const focused = id === focusedId && !suppressed.has(id);
    const handlers = focused ? focusedHandlers : {};

    // Only a socket receiving everything may advance the cursor. A
    // filtered one is shown a subset by the harness, so recording its
    // sequence numbers would tell a later reconnect that the tokens
    // between them had been seen — and the harness would resume past
    // reply text nobody ever rendered.
    if (typeof frame.seq === 'number' && frame.seq > 0
        && harnesses.get(id)?.mode === 'full') {
        patchActivity(id, { lastSeq: frame.seq });
    }

    switch (frame.event) {
        case 'state': {
            const state = (frame.data ?? {}) as Record<string, any>;
            const retry = state.active_turn?.retry as { error?: string; nextAt?: string } | undefined;
            patchActivity(id, {
                status: state.status ?? 'unknown',
                activeTurnId: state.active_turn_id ?? '',
                note: '',
                // A socket opened mid-wait learns of it from the hello.
                statusLine: retry ? `Model unavailable (${retry.error ?? 'no answer'}); retrying` : '',
            });
            handlers.onState?.(state);
            break;
        }
        case 'turn_start': {
            const turn = (frame.data ?? {}) as Record<string, any>;
            patchActivity(id, {
                status: 'running',
                activeTurnId: frame.turn_id ?? turn.turn_id ?? '',
                note: '',
                statusLine: '',
            });
            handlers.onTurnStart?.(frame.turn_id ?? turn.turn_id ?? '', turn.prompt ?? '');
            break;
        }
        case 'turn_end':
            patchActivity(id, { status: 'idle', activeTurnId: '', note: '', statusLine: '' });
            handlers.onTurnEnd?.(frame.turn_id ?? '');
            break;
        case 'cancelling':
            patchActivity(id, { status: 'cancelling' });
            break;
        case 'steer':
            handlers.onSteer?.(String(frame.data ?? ''));
            break;
        case 'delta':
            handlers.onDelta?.(String(frame.data ?? ''));
            break;
        case 'thinking':
            handlers.onThinking?.(String(frame.data ?? ''));
            break;
        case 'tool_started': {
            const tool = (frame.data ?? {}) as Record<string, any>;
            if (tool.name) patchActivity(id, { note: String(tool.name) });
            handlers.onToolStarted?.(
                String(tool.tool_call_id ?? ''), String(tool.name ?? ''), tool.input,
            );
            break;
        }
        case 'tool_completed': {
            const tool = (frame.data ?? {}) as Record<string, any>;
            handlers.onToolCompleted?.(
                String(tool.tool_call_id ?? ''),
                String(tool.output ?? ''),
                tool.is_error === true,
            );
            break;
        }
        case 'tool_ui': {
            const ui = (frame.data ?? {}) as Record<string, any>;
            if (typeof ui.markdown === 'string') {
                handlers.onToolUi?.(String(ui.tool_call_id ?? ''), ui.markdown);
            }
            break;
        }
        case 'status':
            patchActivity(id, { note: String(frame.data ?? ''), statusLine: String(frame.data ?? '') });
            break;
        case 'error':
            handlers.onError?.(String(frame.data ?? ''));
            break;
        default:
            // A Pod on a newer image than this SPA. Ignored, per the
            // same forward-compat contract the SSE surface has.
            break;
    }
}

// ── useSyncExternalStore plumbing ───────────────────────────────────

export function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

export function getSnapshot(): LiveSnapshot {
    return snapshot;
}

/** Activity for one session, or the idle default. */
export function activityOf(state: LiveSnapshot, id: string): SessionActivity {
    return state.activity[id] ?? IDLE;
}
